import { BoxGeometry, CircleGeometry, CylinderGeometry, Group, Mesh, MeshBasicMaterial } from 'three';
import { flatMaterial } from '../../render/materials/cache';
import type { Signal } from '../../systems/junctions/signals';

const LIT = { red: '#ff2b1c', amber: '#ffb300', green: '#27e36a' } as const;
const DIM = { red: '#3a0d0a', amber: '#3a2a07', green: '#0b3316' } as const;
type Lamp = keyof typeof LIT;

const LAMP = new CircleGeometry(0.13, 16);
const HOUSING = new BoxGeometry(0.42, 1.2, 0.3);
const POLE = new CylinderGeometry(0.08, 0.1, 4.6, 8);

/** Lamp materials for one set of heads; every head in the set shows the same signal. */
class LampSet {
  readonly materials: Record<Lamp, MeshBasicMaterial> = {
    red: new MeshBasicMaterial({ color: DIM.red }),
    amber: new MeshBasicMaterial({ color: DIM.amber }),
    green: new MeshBasicMaterial({ color: DIM.green }),
  };

  show(signal: Signal): void {
    for (const lamp of Object.keys(this.materials) as Lamp[]) {
      this.materials[lamp].color.set(lamp === signal ? LIT[lamp] : DIM[lamp]);
    }
  }
}

/** A pole with a three-lamp head facing +z (towards traffic approaching from +z), rotated by `rotY`. */
function pole(lamps: LampSet, x: number, z: number, rotY: number): Group {
  const group = new Group();
  const post = new Mesh(POLE, flatMaterial('#5c6166'));
  post.position.y = 2.3;
  const head = new Group();
  head.position.y = 4.9;
  const housing = new Mesh(HOUSING, flatMaterial('#141414'));
  housing.castShadow = true;
  const backplate = new Mesh(new BoxGeometry(0.7, 1.5, 0.04), flatMaterial('#141414'));
  backplate.position.z = -0.17;
  head.add(backplate, housing);
  (['red', 'amber', 'green'] as const).forEach((lamp, i) => {
    const light = new Mesh(LAMP, lamps.materials[lamp]);
    light.position.set(0, 0.36 - i * 0.36, 0.16);
    head.add(light);
  });
  group.add(post, head);
  group.position.set(x, 0, z);
  group.rotation.y = rotY;
  return group;
}

export interface LightPositions {
  /** Poles facing our traffic (kerb corner and median). */
  ours: readonly { x: number; z: number }[];
  /** Poles facing the crossing road, one per side. */
  cross: readonly { x: number; z: number; rotY: number }[];
}

/** The traffic lights of one junction. */
export class TrafficLights {
  readonly group = new Group();
  private readonly ours = new LampSet();
  private readonly cross = new LampSet();

  constructor(positions: LightPositions) {
    for (const p of positions.ours) this.group.add(pole(this.ours, p.x, p.z, 0));
    for (const p of positions.cross) this.group.add(pole(this.cross, p.x, p.z, p.rotY));
  }

  show(ourSignal: Signal, crossSignal: Signal): void {
    this.ours.show(ourSignal);
    this.cross.show(crossSignal);
  }
}
